import React, { useState } from "react";
import { Box, Typography, Grid, Paper, MenuItem, Select, Card, CardContent } from "@mui/material";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export const CPODashboard = () => {
  const [filter, setFilter] = useState("monthly"); // Filter for monthly/yearly view

  // Dummy data for the graphs
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const productCategories = ["Electronics", "Fashion", "Home Appliances", "Books", "Others"];

  const productPerformanceData = {
    labels: months,
    datasets: [
      {
        label: "Feature Usage",
        data: [50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160],
        borderColor: "#4caf50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        tension: 0.4,
      },
      {
        label: "Bug Reports",
        data: [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65],
        borderColor: "#f44336",
        backgroundColor: "rgba(244, 67, 54, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const customerRetentionData = {
    labels: months,
    datasets: [
      {
        label: "Retention Rate",
        data: [80, 82, 85, 87, 88, 90, 92, 93, 94, 95, 96, 97],
        borderColor: "#2196f3",
        backgroundColor: "rgba(33, 150, 243, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const revenueByCategoryData = {
    labels: productCategories,
    datasets: [
      {
        label: "Revenue",
        data: [50000, 30000, 20000, 15000, 10000],
        backgroundColor: ["#4caf50", "#2196f3", "#ff9800", "#9c27b0", "#f44336"],
      },
    ],
  };

  const featureAdoptionData = {
    labels: months,
    datasets: [
      {
        label: "Feature Adoption",
        data: [30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140],
        borderColor: "#9c27b0",
        backgroundColor: "rgba(156, 39, 176, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  return (
    <Box sx={{ padding: "24px" }}>
      {/* Page Title */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        Chief Product Officer Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ color: "#2196f3", fontWeight: "bold" }}>
                $2,500,000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Active Users
              </Typography>
              <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: "bold" }}>
                12,000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Retention Rate
              </Typography>
              <Typography variant="h4" sx={{ color: "#2196f3", fontWeight: "bold" }}>
                95%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Feature Adoption
              </Typography>
              <Typography variant="h4" sx={{ color: "#9c27b0", fontWeight: "bold" }}>
                85%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Section */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
        <Select
          value={filter}
          onChange={handleFilterChange}
          sx={{ minWidth: "150px" }}
        >
          <MenuItem value="monthly">Monthly View</MenuItem>
          <MenuItem value="yearly">Yearly View</MenuItem>
        </Select>
      </Box>

      {/* Graphs Section */}
      <Grid container spacing={3}>
        {/* Product Performance */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Product Performance
            </Typography>
            <Line data={productPerformanceData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Customer Retention */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Customer Retention Rate
            </Typography>
            <Line data={customerRetentionData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Revenue by Category */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Revenue by Product Category
            </Typography>
            <Doughnut data={revenueByCategoryData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Feature Adoption */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Feature Adoption Over Time
            </Typography>
            <Line data={featureAdoptionData} options={{ responsive: true }} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CPODashboard;